import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Wrench, ShoppingCart, Plus, Minus, Trash2, ArrowLeft, Package, Phone, CheckCircle, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HardwareStorePage() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [orderForm, setOrderForm] = useState({ name: '', phone: '', address: '', notes: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/hardware`).then(r => setItems(r.data || [])).catch(() => {});
  }, []);

  const addToCart = (item) => {
    setCart(prev => {
      const exists = prev.find(c => c.id === item.id);
      if (exists) return prev.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { id: item.id, name: item.name, price: Number(item.price || 0), qty: 1, image: item.image }];
    });
    toast({ title: language === 'en' ? `${item.name} added to cart` : `${item.name} কার্টে যোগ হয়েছে` });
  };

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(c => c.id === id ? { ...c, qty: Math.max(1, c.qty + delta) } : c));
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(c => c.id !== id));
  };

  const cartTotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  const submitOrder = async () => {
    const errors = {};
    if (!orderForm.name.trim()) errors.name = true;
    if (!/^01\d{9}$/.test(orderForm.phone)) errors.phone = true;
    if (!orderForm.address.trim()) errors.address = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/hardware-orders`, {
        customer_name: orderForm.name.trim(),
        customer_phone: orderForm.phone.trim(),
        customer_address: orderForm.address.trim(),
        notes: orderForm.notes.trim(),
        items: cart.map(c => ({ id: c.id, name: c.name, price: c.price, qty: c.qty })),
      });
      setSubmitted(true);
      setCart([]);
    } catch {
      toast({ title: language === 'en' ? 'Failed to submit order' : 'অর্ডার জমা দেওয়া যায়নি', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mp-brand min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 font-['Plus_Jakarta_Sans']">
      <style>{`
        .mp-brand .text-emerald-500 { color: #009137 !important; }
        .mp-brand .text-emerald-600 { color: #009137 !important; }
        .mp-brand .text-emerald-700 { color: #013C10 !important; }
        .mp-brand .bg-emerald-50 { background-color: rgba(0,145,55,0.08) !important; }
        .mp-brand .bg-emerald-50\\/30 { background-color: rgba(0,145,55,0.05) !important; }
        .mp-brand .bg-emerald-100 { background-color: rgba(0,145,55,0.14) !important; }
        .mp-brand .bg-emerald-600 {
          background-image: linear-gradient(135deg, #013C10 0%, #009137 100%) !important;
          background-color: #013C10 !important;
        }
        .mp-brand .hover\\:bg-emerald-700:hover {
          background-image: linear-gradient(135deg, #002a08 0%, #007a2e 100%) !important;
          background-color: #002a08 !important;
        }
        .mp-brand .hover\\:bg-emerald-50:hover { background-color: rgba(0,145,55,0.10) !important; }
        .mp-brand .border-emerald-100 { border-color: rgba(1,60,16,0.18) !important; }
        .mp-brand .border-emerald-200 { border-color: rgba(1,60,16,0.28) !important; }
        .mp-brand .border-emerald-300 { border-color: rgba(0,145,55,0.45) !important; }
        .mp-brand .hover\\:border-emerald-300:hover { border-color: rgba(0,145,55,0.55) !important; }
        .mp-brand .shadow-emerald-100\\/40 { --tw-shadow-color: rgba(1,60,16,0.18) !important; --tw-shadow: var(--tw-shadow-colored) !important; }
        .mp-brand .shadow-emerald-200\\/50 { --tw-shadow-color: rgba(0,145,55,0.32) !important; --tw-shadow: var(--tw-shadow-colored) !important; }
      `}</style>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundImage: 'linear-gradient(135deg, #013C10 0%, #009137 100%)' }}><Wrench className="w-4 h-4 text-white" /></div>
            <span className="text-lg font-[900] font-['Epilogue'] text-slate-900">Motopotti</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-slate-500 text-xs"><ArrowLeft className="w-3.5 h-3.5 mr-1" />{language === 'en' ? 'Home' : 'হোম'}</Button>
            </Link>
            <button onClick={() => setShowCart(true)} className="relative flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-colors">
              <ShoppingCart className="w-4 h-4" />
              {language === 'en' ? 'Cart' : 'কার্ট'}
              {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{cartCount}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 sm:py-20 px-4 sm:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-bold tracking-wider uppercase px-4 py-1.5">
            <Package className="w-3 h-3 mr-1.5" />{language === 'en' ? 'Hardware Store' : 'হার্ডওয়্যার স্টোর'}
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-[900] font-['Epilogue'] text-slate-900 tracking-tight mb-4">
            {language === 'en' ? 'Digitize Your Garage' : 'গ্যারেজ ডিজিটাল করুন'}
          </h1>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            {language === 'en' ? 'Essential hardware to run your garage digitally. We\'ll help you set everything up.' : 'গ্যারেজ ডিজিটালি চালাতে প্রয়োজনীয় হার্ডওয়্যার। সেটআপে আমরা সাহায্য করব।'}
          </p>
        </div>
      </section>

      {/* Products grid */}
      <section className="px-4 sm:px-8 pb-24">
        <div className="max-w-7xl mx-auto">
          {items.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="font-medium">{language === 'en' ? 'No products available yet' : 'এখনও কোনো পণ্য নেই'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map(item => {
                const inCart = cart.find(c => c.id === item.id);
                return (
                  <div key={item.id} className="group bg-white border border-slate-200/80 rounded-2xl overflow-hidden hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-100/40 transition-all duration-300">
                    {(item.image || item.image_url) && (
                      <div className="aspect-video bg-slate-100 overflow-hidden">
                        <img src={item.image || item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="font-[800] text-slate-900 font-['Epilogue'] mb-1">{item.name}</h3>
                      {item.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{item.description}</p>}
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xl font-[900] text-emerald-600 font-['Epilogue']">৳{Number(item.price || 0).toLocaleString()}</span>
                        {item.bundle_tag && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">{item.bundle_tag}</Badge>}
                      </div>
                      {inCart ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center border border-emerald-200 rounded-xl overflow-hidden flex-1">
                            <button onClick={() => updateQty(item.id, -1)} className="px-3 py-2 hover:bg-emerald-50 transition-colors"><Minus className="w-3.5 h-3.5 text-emerald-600" /></button>
                            <span className="flex-1 text-center text-sm font-bold text-slate-900">{inCart.qty}</span>
                            <button onClick={() => updateQty(item.id, 1)} className="px-3 py-2 hover:bg-emerald-50 transition-colors"><Plus className="w-3.5 h-3.5 text-emerald-600" /></button>
                          </div>
                          <button onClick={() => removeFromCart(item.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <Button onClick={() => addToCart(item)} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl text-sm font-bold">
                          <ShoppingCart className="w-4 h-4 mr-1.5" />{language === 'en' ? 'Add to Cart' : 'কার্টে যোগ করুন'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Floating cart button for mobile */}
      {cartCount > 0 && (
        <div className="fixed bottom-6 right-6 md:hidden z-40">
          <button onClick={() => setShowCart(true)} className="flex items-center gap-2 bg-emerald-600 text-white px-5 py-3 rounded-2xl shadow-xl shadow-emerald-200/50 font-bold text-sm">
            <ShoppingCart className="w-5 h-5" />
            {cartCount} {language === 'en' ? 'items' : 'পণ্য'} · ৳{cartTotal.toLocaleString()}
          </button>
        </div>
      )}

      {/* Cart dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Epilogue'] font-[800] flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-emerald-600" />
              {language === 'en' ? 'Your Cart' : 'আপনার কার্ট'}
            </DialogTitle>
          </DialogHeader>

          {submitted ? (
            <div className="py-10 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-[800] font-['Epilogue'] text-slate-900 mb-2">{language === 'en' ? 'Order Submitted!' : 'অর্ডার জমা হয়েছে!'}</h3>
              <p className="text-slate-500 text-sm mb-6">{language === 'en' ? 'Our team will contact you shortly to confirm your order.' : 'আমাদের টিম শীঘ্রই আপনার অর্ডার নিশ্চিত করতে যোগাযোগ করবে।'}</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => { setSubmitted(false); setShowCart(false); }} className="rounded-xl">{language === 'en' ? 'Continue Shopping' : 'কেনাকাটা চালিয়ে যান'}</Button>
                <Link to="/"><Button className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">{language === 'en' ? 'Go Home' : 'হোমে যান'}</Button></Link>
              </div>
            </div>
          ) : cart.length === 0 ? (
            <div className="py-10 text-center text-slate-400">
              <ShoppingCart className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">{language === 'en' ? 'Your cart is empty' : 'আপনার কার্ট খালি'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cart items */}
              <div className="space-y-3">
                {cart.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    {c.image && <img src={c.image} alt={c.name} className="w-14 h-14 rounded-lg object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-slate-900 truncate">{c.name}</p>
                      <p className="text-xs text-emerald-600 font-[800]">৳{c.price.toLocaleString()} × {c.qty}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQty(c.id, -1)} className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100"><Minus className="w-3 h-3" /></button>
                      <span className="w-6 text-center text-sm font-bold">{c.qty}</span>
                      <button onClick={() => updateQty(c.id, 1)} className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100"><Plus className="w-3 h-3" /></button>
                      <button onClick={() => removeFromCart(c.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 ml-1"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t border-dashed border-slate-200 pt-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm font-bold text-slate-700">{language === 'en' ? 'Total' : 'মোট'}</span>
                  <span className="text-2xl font-[900] font-['Epilogue'] text-slate-900">৳{cartTotal.toLocaleString()}</span>
                </div>
              </div>

              {/* Order form */}
              <div className="border-t border-slate-200 pt-4 space-y-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Delivery Details' : 'ডেলিভারি বিবরণ'}</p>
                <div>
                  <Label className="text-xs text-slate-500">{language === 'en' ? 'Full Name' : 'পূর্ণ নাম'} *</Label>
                  <Input value={orderForm.name} onChange={e => { setOrderForm(p => ({ ...p, name: e.target.value })); setFormErrors(p => ({ ...p, name: false })); }} className={`mt-1 ${formErrors.name ? 'border-red-500' : ''}`} placeholder={language === 'en' ? 'Your name' : 'আপনার নাম'} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{language === 'en' ? 'Phone Number' : 'ফোন নম্বর'} *</Label>
                  <Input value={orderForm.phone} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 11); setOrderForm(p => ({ ...p, phone: v })); setFormErrors(p => ({ ...p, phone: false })); }} className={`mt-1 ${formErrors.phone ? 'border-red-500' : ''}`} placeholder="01XXXXXXXXX" />
                  {formErrors.phone && <p className="text-[10px] text-red-500 mt-0.5">{language === 'en' ? '11 digits starting with 01' : '০১ দিয়ে শুরু ১১ সংখ্যা'}</p>}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{language === 'en' ? 'Delivery Address' : 'ডেলিভারি ঠিকানা'} *</Label>
                  <Textarea value={orderForm.address} onChange={e => { setOrderForm(p => ({ ...p, address: e.target.value })); setFormErrors(p => ({ ...p, address: false })); }} className={`mt-1 ${formErrors.address ? 'border-red-500' : ''}`} rows={2} placeholder={language === 'en' ? 'Full address' : 'পূর্ণ ঠিকানা'} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{language === 'en' ? 'Notes (optional)' : 'নোটস (ঐচ্ছিক)'}</Label>
                  <Input value={orderForm.notes} onChange={e => setOrderForm(p => ({ ...p, notes: e.target.value }))} className="mt-1" placeholder={language === 'en' ? 'Any special instructions' : 'বিশেষ কোনো নির্দেশনা'} />
                </div>
              </div>

              <Button onClick={submitOrder} disabled={submitting} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-xl font-bold text-base">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{language === 'en' ? 'Submitting...' : 'জমা হচ্ছে...'}</> : <>{language === 'en' ? 'Submit Order' : 'অর্ডার জমা দিন'} · ৳{cartTotal.toLocaleString()}</>}
              </Button>

              <p className="text-[10px] text-slate-400 text-center">{language === 'en' ? 'Our team will contact you to confirm your order and arrange delivery.' : 'আমাদের টিম অর্ডার নিশ্চিত এবং ডেলিভারি ব্যবস্থা করতে যোগাযোগ করবে।'}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-10 px-4 sm:px-8 text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center"><Wrench className="w-4 h-4 text-white" /></div>
          <span className="text-lg font-[900] font-['Epilogue']">Motopotti</span>
        </Link>
        <p className="text-slate-400 text-sm">{language === 'en' ? 'Need help? Call us:' : 'সাহায্য দরকার? কল করুন:'} <a href="tel:+8801786264886" className="text-emerald-400 font-bold hover:underline">01786264886</a></p>
        <p className="text-slate-600 text-xs mt-4">© 2026 MotoPotti</p>
      </footer>
    </div>
  );
}
